import React, { useCallback, useRef, useState } from 'react'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import RadioInput from 'LuxKit/Radio/RadioInput'
import SwitchButton from 'LuxKit/Switch/SwitchButton'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import styled from 'styled-components'
import { rem } from 'polished'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

interface Props {
  nights: number
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  luxPlusEnabled?: boolean
  onLuxPlusToggle?: (enabled: boolean) => void
}

/**
 * Header actions for Charter Bundle Calendar Modal
 * Contains duration dropdown and LuxPlus toggle
 */
function CharterBundleCalendarActions(props: Props) {
  const {
    nights,
    onDurationChange,
    durationOptions,
    luxPlusEnabled = true,
    onLuxPlusToggle,
  } = props

  const [isDurationDropdownOpen, setIsDurationDropdownOpen] = useState(false)
  const durationTriggerRef = useRef<HTMLButtonElement>(null)

  const handleDurationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newDuration = parseInt(event.target.value, 10)
    onDurationChange(newDuration)
    setIsDurationDropdownOpen(false)
  }, [onDurationChange])

  const toggleDurationDropdown = useCallback(() => {
    setIsDurationDropdownOpen(prev => !prev)
  }, [])

  const closeDurationDropdown = useCallback(() => {
    setIsDurationDropdownOpen(false)
  }, [])

  const handleLuxPlusToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onLuxPlusToggle?.(event.currentTarget.checked)
  }, [onLuxPlusToggle])

  return (
    <Group direction="horizontal" verticalAlign="center" gap={16}>
      <div>
        <FilterChip
          size="medium"
          onClick={toggleDurationDropdown}
          endIcon={<DropdownChevron open={isDurationDropdownOpen} />}
          ref={durationTriggerRef}
        >
          {durationOptions.find(option => option.value === nights)?.label || '5 nights + flights'}
        </FilterChip>
        <DropdownList
          size="S"
          open={isDurationDropdownOpen}
          onClose={closeDurationDropdown}
          anchorRef={durationTriggerRef}
          triggerRef={durationTriggerRef}
        >
          <RadioGroup direction="vertical" gap={16}>
            {durationOptions.map((option) => (
              <RadioInput
                key={option.value}
                checked={nights === option.value}
                onChange={handleDurationChange}
                name="durationNights"
                value={option.value}
              >
                {option.label}
              </RadioInput>
            ))}
          </RadioGroup>
        </DropdownList>
      </div>
      <ProductPaletteProvider palette="lux-plus">
        <SwitchButton
          checked={luxPlusEnabled}
          data-testid="luxPlus-toggle"
          onChange={handleLuxPlusToggle}
        >
          <LuxPlusLogo inline height={10} />
        </SwitchButton>
      </ProductPaletteProvider>
    </Group>
  )
}

export default CharterBundleCalendarActions
